import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  ChevronRight,
  DateRangePicker,
  HighlightCard,
  Info,
  PageHeader,
  Pill,
  StatsCard,
  Table,
} from '@demandlocal/components'
import { ReactComponent as DownloadIcon } from '../../../../images/icons/action_download.svg'
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from '../../../../lib/utils/formatNumber'
import classNames from 'classnames'
import { isEmpty } from 'lodash'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { createColumnHelper } from '@tanstack/react-table'
import { useDownloadPDFReport } from '../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { useGetSalePathsOverallMetrics } from '../../../../api/dealership/performance/salePaths/useGetSalePathsOverallMetrics'
import dayjs from 'dayjs'
import { useGetSalePathsVerifiedSales } from '../../../../api/dealership/performance/salePaths/useGetSalePathsVerifiedSales'
import { useGetSalePathsImpressions } from '../../../../api/dealership/performance/salePaths/useGetSalePathsImpressions'
import PolarAreaChart from '../../../../components/Charts/PolarAreaChart'
import { MapContainer, TileLayer, Circle } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import MarkerClusterGroup from '@changey/react-leaflet-markercluster'
import L from 'leaflet'
import { useGetVerifiedLeads } from '../../../../api/dealership/performance/salePaths/useGetVerifiedLeads'
import { CustomerRow } from '../../../../lib/types/CustomerRow'
import SalesUserPath from '../../../../components/Performance/SalesPaths/SalesUserPath'
import LeadUserPath from '../../../../components/Performance/LeadPaths/LeadUserPath'
import { useDateRange } from '../../../../store/zustand/dateRangeStore'
import { DateRange } from '../../../../lib/types/DateRange'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import downloadCSV from '../../../../api/downloadCSV'
import { useTranslation } from 'react-i18next'
import HorizontalBarChart from '../../../../components/Charts/HorizontalBarChart'
import { useGetUTMSources } from '../../../../api/dealership/performance/salePaths/useGetUTMSources'
import { match } from 'ts-pattern'
import { motion } from 'framer-motion'
import { useGetCustomerSourcesByDealershipID } from '../../../../api/dealership/customers/sources/useGetCustomerSourcesByDealershipID'
import BarChart from '../../../../components/Charts/BarChart'

const createClusterCustomIcon = function (cluster: {
  getChildCount: () => number | string
}): L.DivIcon {
  return L.divIcon({
    html: `<span>${cluster.getChildCount()}</span>`,
    className: 'border-2 border-red-500 p-2 rounded-full bg-white shadow-md text-center',
    iconSize: L.point(40, 40, true),
  })
}

const MapWithBubbles = ({
  data,
  isLoading,
}: {
  data: Array<{ LATITUDE: number; LONGITUDE: number }>
  isLoading: boolean
}): JSX.Element | null => {
  if (isLoading) {
    return (
      <div className={'w-full h-full flex items-center justify-center'}>Loading...</div>
    )
  }
  if (isEmpty(data)) return null

  return (
    <>
      <MapContainer
        bounds={L.latLngBounds(data?.map((coord) => [coord.LATITUDE, coord.LONGITUDE]))}
        zoom={13}
        style={{ height: '100%', width: '100%', zIndex: 0 }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <MarkerClusterGroup
          showCoverageOnHover={false}
          spiderfyDistanceMultiplier={2}
          iconCreateFunction={createClusterCustomIcon}>
          {data.map((coord, idx) => (
            <Circle
              key={idx}
              center={[coord.LATITUDE, coord.LONGITUDE]}
              radius={10}
              color="blue"
            />
          ))}
        </MarkerClusterGroup>
      </MapContainer>
    </>
  )
}

const verifiedSalesColumnHelper = createColumnHelper<CustomerRow>()
const topSitesColumnHelper = createColumnHelper<{
  utm_source: string
  site: string
  people: number
  percentage_of_total: number
  average: number
}>()

export default function PerformanceSalePaths(): JSX.Element {
  const dealership = useDealership()
  const { dateRange, setDateRange } = useDateRange()
  const [queryParams, setQueryParams] = useSearchParams()
  const [downloadingTable, setDownloadingTable] = useState<boolean>(false)
  const currentDealership = useDealership()
  const location = useLocation()
  const navigate = useNavigate()
  const [topSitesExpanded, setTopSitesExpanded] = useState<boolean>(false)
  const pathGoal = useMemo(
    () => location.pathname.split('/').pop() ?? 'sales',
    [location.pathname],
  ) as 'sales' | 'leads'

  const { t: routes } = useTranslation('routes')

  const { t } = useTranslation('pages', {
    keyPrefix: `performance.salePaths.${currentDealership?.industry || 'automotive'}`,
  })

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  useEffect(() => {
    setQueryParams(filters.dateRange)
  }, [filters, setQueryParams])

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  useEffect(() => {
    if (!['sales', 'leads'].includes(pathGoal)) navigate('sales')
  }, [navigate, pathGoal])

  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()

  const {
    data: salePathsMetrics,
    isPreviousData: isPreviousDataOverallMetrics,
    isLoading: isLoadingSalesPathsMetrics,
  } = useGetSalePathsOverallMetrics({
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })

  const { data: sources, isLoading: isLoadingSources } =
    useGetCustomerSourcesByDealershipID({
      pageIndex: 1,
      pageSize: 10,
      sorting: [{ id: 'updated_date', sort: 'desc' }],
    })

  const hasDMS = useMemo(() => {
    return sources?.data?.some((s) => s.customer_source_type === 'Sales')
  }, [sources])

  const hasCRM = useMemo(() => {
    return sources?.data?.some((s) => s.customer_source_type === 'Leads')
  }, [sources])

  const {
    data: {
      data: impressionsData,
      insights: {
        topSites = [],
        campaignImpressions = [],
        deviceImpressions = [],
        campaignSales = [],
        audienceSales = [],
        vendorCampaignSales = [],
        uniqueDevicesPerConversion = 0,
        mostEfficientDevice,
        uniqueSitesPerConversion = 0,
        mostEfficientSite,
        shortestConversionTimeSite,
        dayDifference,
        impressionRangeCounts,
        impressionDecayAnalysis,
      } = {},
    } = {},
    isPreviousData: isPreviousImpressionData,
    isLoading: isLoadingImpressionData,
  } = useGetSalePathsImpressions({
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
      goal: pathGoal,
    },
  })

  const { data: verifiedLeads, isLoading: isLoadingVerifiedLeads } = useGetVerifiedLeads({
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
      excludeSales: 0,
    },
  })

  const isLoadingReport = useMemo(
    () => isLoadingSalesPathsMetrics || isLoadingImpressionData || isLoadingVerifiedLeads,
    [isLoadingImpressionData, isLoadingVerifiedLeads, isLoadingSalesPathsMetrics],
  )

  useEffect(() => {
    if (!isLoadingReport) {
      setTimeout(() => {
        const content = document.getElementById('content')
        if (content) content.className = `${content.className} loaded`
      }, 5000)
    }
  }, [isLoadingReport])

  const potentialSalesColumns = useMemo(
    () => [
      verifiedSalesColumnHelper.accessor('masked_pii', {
        header: 'Customer',
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <button
              className={'font-bold text-xs text-primary-persian-blue'}
              {...{
                onClick: (event) => {
                  event.preventDefault()
                  row.toggleExpanded()
                },
                style: { cursor: 'pointer' },
              }}>
              {row.original.masked_pii}
            </button>
          ) : (
            <span className="font-bold text-xs text-primary-persian-blue">
              {row.original.masked_pii}
            </span>
          )
        },
      }),
      verifiedSalesColumnHelper.accessor('impressions', {
        header: 'Impressions',
        cell: (info) => formatNumber(info.getValue()),
      }),
      verifiedSalesColumnHelper.accessor('leads', {
        header: () => (
          <div className="flex flex-row gap-2 items-center">
            <span>{t('leads')}s</span>
            <Info
              message={`Number of ${t(
                'leads',
              ).toLocaleLowerCase()} submitted in the selected date period`}
            />
          </div>
        ),
        cell: (info) => formatNumber(info.getValue()),
      }),
      verifiedSalesColumnHelper.accessor('first_impression', {
        header: 'First Impression',
        cell: (info) => dayjs(info.getValue()).format('MMM D, YYYY'),
      }),
      verifiedSalesColumnHelper.accessor('last_lead_date', {
        header: 'Last Lead Date',
        cell: (info) => dayjs(info.getValue()).format('MMM D, YYYY'),
      }),
    ],
    [t],
  )

  const verifiedSalesColumns = useMemo(
    () => [
      verifiedSalesColumnHelper.accessor('masked_pii', {
        header: 'Customer',
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <button
              className={'font-bold text-xs text-primary-persian-blue'}
              {...{
                onClick: (event) => {
                  event.preventDefault()
                  row.toggleExpanded()
                },
                style: { cursor: 'pointer' },
              }}>
              {row.original.masked_pii}
            </button>
          ) : (
            <span className="font-bold text-xs text-primary-persian-blue">
              {row.original.masked_pii}
            </span>
          )
        },
      }),
      verifiedSalesColumnHelper.accessor('sale_date', {
        header: `Sale Date`,
        cell: (info) => dayjs(info.getValue()).format('MMM D, YYYY'),
      }),
      ...(dealership?.industry === 'retail'
        ? []
        : [
            verifiedSalesColumnHelper.accessor('vin', {
              header: 'Vehicle VIN',
              cell: (info) => info.getValue(),
            }),
            verifiedSalesColumnHelper.display({
              id: 'vehicle',
              header: 'Vehicle',
              cell: ({
                row: {
                  original: { year, make, model },
                },
              }) =>
                `${Boolean(year) ? year : ''} ${make ? make : ''} ${model ? model : ''}`,
            }),
          ]),
      verifiedSalesColumnHelper.accessor('impressions', {
        header: 'Pre Sale Impressions',
        cell: (info) => info.getValue(),
      }),
      verifiedSalesColumnHelper.accessor('first_impression', {
        header: 'First Impression',
        cell: (info) => dayjs(info.getValue()).format('YYYY-MM-DD'),
      }),
      verifiedSalesColumnHelper.accessor('last_impression', {
        header: 'Last Impression',
        cell: (info) => dayjs(info.getValue()).format('YYYY-MM-DD'),
      }),
    ],
    [dealership?.industry],
  )

  const topSitesColumns = useMemo(
    () => [
      topSitesColumnHelper.accessor('utm_source', {
        header: 'UTM Source',
        cell: (info) => info.getValue(),
      }),
      topSitesColumnHelper.accessor('people', {
        header: 'People',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
      topSitesColumnHelper.accessor('percentage_of_total', {
        header: '% of Total',
        cell: (info) => formatPercentage(info.getValue() || 0, 1),
      }),
      topSitesColumnHelper.accessor('average', {
        header: 'Avg / Person',
        cell: (info) =>
          formatNumber(info.getValue() || 0, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
          }),
      }),
    ],
    [],
  )

  const topSitesData = useMemo(
    () =>
      topSites
        .sort((a, b) => (a.people < b.people ? 1 : -1))
        ?.slice(0, topSitesExpanded ? 25 : 10) ?? [],
    [topSites, topSitesExpanded],
  )

  return (
    <div className={'flex flex-1 flex-col'} id={'content'}>
      <PageHeader
        cardsOptions={[]}
        title={`Path to ${t(pathGoal)}s`}
        rightSlot={() => (
          <div className={'flex gap-2 print:hidden'}>
            <DateRangePicker
              // UID2 REDs started 7/9/24
              minValue={'2024-07-01'}
              filters={filters}
              onChange={onDateRangeChange}
            />
            <Button
              variant={'rainbow'}
              disabled={isDownloadingReport}
              rightSlot={<DownloadIcon className={'fill-white'} />}
              onClick={async (e) => {
                const toastId = toast.loading('Generating report...')
                await downloadReport(undefined, {
                  async onSuccess(data) {
                    toast.success('Report generated successfully', { id: toastId })
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `PerformanceSalesPaths_${filters.dateRange.startDate}_${filters.dateRange.endDate}.pdf`
                    document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                    a.click()
                    a.remove()
                  },
                })
              }}>
              Export PDF
            </Button>
          </div>
        )}
      />
      <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
        <div className={'flex flex-col gap-8 pb-8'}>
          <div
            className="text-md rounded-xl p-2 bg-gainsboro-gray/40 border-2"
            dangerouslySetInnerHTML={{
              __html: t(`description.${pathGoal}`),
            }}
          />

          <div
            className={classNames('grid grid-cols-2 auto-rows-fr gap-4', {
              'opacity-50': isPreviousDataOverallMetrics,
            })}>
            <HighlightCard
              value={formatPercentage(salePathsMetrics?.uid2Ratio, 1)}
              content={
                <div>
                  Ratio of trackable impressions to total programmatic impressions
                </div>
              }
            />
            <HighlightCard
              value={formatCurrency(salePathsMetrics?.mediaSpend)}
              content={<div>Total spend on programmatic media</div>}
            />
          </div>
          <div
            className={classNames('grid grid-cols-2 auto-rows-fr gap-4', {
              'opacity-50': isPreviousDataOverallMetrics,
            })}>
            {isLoadingSources || hasDMS ? (
              <div className={'rounded-xl h-[100px] items-center flex '}>
                <StatsCard
                  title={`Verified ${t('sales')}s`}
                  content={
                    isLoadingSalesPathsMetrics ? (
                      <div>-</div>
                    ) : (
                      <div className="flex flex-row justify-between items-center">
                        <div>
                          {formatNumber(salePathsMetrics?.verifiedSalesCount)} /{' '}
                          {formatNumber(salePathsMetrics?.totalSales)}
                        </div>
                        <div>
                          <Pill type="info-purple" variant="rounded">
                            {!Boolean(salePathsMetrics?.verifiedSalesCount) ||
                            !Boolean(salePathsMetrics?.totalSales)
                              ? 0
                              : formatPercentage(
                                  Number(salePathsMetrics?.verifiedSalesCount) /
                                    Number(salePathsMetrics?.totalSales),
                                  1,
                                )}
                          </Pill>
                        </div>
                      </div>
                    )
                  }
                  color={classNames('bg-secondary-malachite-green/40', {
                    'opacity-50': isLoadingSalesPathsMetrics,
                    'border-2 border-secondary-malachite-green': pathGoal === 'sales',
                  })}
                  info={`Users seen in the DMS who were exposed to an ad and ${t(
                    'info_verb.sales',
                  )}`}
                  hasAnimatedDescriptionOverflow
                  description="Click here to see full details"
                  to="sales"
                />
              </div>
            ) : (
              <div
                className="border-2 h-[100px] border-dashed border-primary-persian-blue/10 bg-primary-persian-blue/5 rounded-md flex justify-center items-center cursor-pointer"
                onClick={() =>
                  navigate(
                    `${routes('dealership.customers.sources.create', {
                      dealership,
                    })}/sales`,
                  )
                }>
                <span className="font-bold text-primary-persian-blue text-xs">
                  + Connect your DMS to see verified sales
                </span>
              </div>
            )}
            {isLoadingSources || hasCRM ? (
              <div className={'rounded-xl h-[100px] items-center flex'}>
                <StatsCard
                  title={`Verified ${t('leads')}s`}
                  content={
                    isLoadingSalesPathsMetrics ? (
                      <div>-</div>
                    ) : (
                      <div className="flex flex-row justify-between items-center">
                        <div>
                          {formatNumber(salePathsMetrics?.verifiedLeadsCount)} /{' '}
                          {formatNumber(salePathsMetrics?.totalLeads)}
                        </div>
                        <div>
                          <Pill type="info-purple" variant="rounded">
                            {!Boolean(salePathsMetrics?.verifiedLeadsCount) ||
                            !Boolean(salePathsMetrics?.totalLeads)
                              ? 0
                              : formatPercentage(
                                  Number(salePathsMetrics?.verifiedLeadsCount) /
                                    Number(salePathsMetrics?.totalLeads),
                                  1,
                                )}
                          </Pill>
                        </div>
                      </div>
                    )
                  }
                  color={classNames('bg-secondary-malachite-green/40', {
                    'opacity-50': isLoadingSalesPathsMetrics,
                    'border-2 border-secondary-malachite-green': pathGoal === 'leads',
                  })}
                  hasAnimatedDescriptionOverflow
                  info="Users seen in the CRM who were exposed to an ad"
                  description="Click here to see full details"
                  to="leads"
                />
              </div>
            ) : (
              <div
                className="border-2 h-[100px] border-dashed border-primary-persian-blue/10 bg-primary-persian-blue/5 rounded-md flex justify-center items-center cursor-pointer"
                onClick={() =>
                  navigate(
                    `${routes('dealership.customers.sources.create', {
                      dealership,
                    })}/leads`,
                  )
                }>
                <span className="font-bold text-primary-persian-blue text-xs">
                  + Connect your CRM to see verified leads
                </span>
              </div>
            )}
          </div>
          <div className={classNames('grid grid-cols-3 auto-rows-fr gap-4')}>
            <StatsCard
              title={`Avg. Imp per ${t(pathGoal)}`}
              content={formatNumber(
                match(pathGoal)
                  .with('sales', () =>
                    salePathsMetrics?.verifiedSalesCount
                      ? (impressionsData?.length ?? 0) /
                        salePathsMetrics?.verifiedSalesCount
                      : 0,
                  )
                  .with('leads', () =>
                    salePathsMetrics?.verifiedLeadsCount
                      ? (impressionsData?.length ?? 0) /
                        salePathsMetrics?.verifiedLeadsCount
                      : 0,
                  )
                  .otherwise(() => 0),

                {
                  maximumFractionDigits: 2,
                },
              )}
              color={classNames('bg-secondary-malachite-green/40', {
                'opacity-50':
                  isPreviousImpressionData ||
                  isLoadingImpressionData ||
                  isLoadingSalesPathsMetrics ||
                  isPreviousDataOverallMetrics,
              })}
            />
            <StatsCard
              title={`Avg. Days From First Imp to ${t(pathGoal)}`}
              content={formatNumber(
                Number(dayDifference?.totalFirstDiff) / Number(dayDifference?.count) || 0,
                {
                  maximumFractionDigits: 2,
                },
              )}
              color={classNames('bg-secondary-malachite-green/40', {
                'opacity-50': isPreviousImpressionData || isLoadingImpressionData,
              })}
            />
            <StatsCard
              title={`Avg. Days From Last Imp to ${t(pathGoal)}`}
              content={formatNumber(
                Number(dayDifference?.totalLastDiff) / Number(dayDifference?.count) || 0,
                {
                  maximumFractionDigits: 2,
                },
              )}
              color={classNames('bg-secondary-malachite-green/40', {
                'opacity-50': isPreviousImpressionData || isLoadingImpressionData,
              })}
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <HighlightCard
              value={formatNumber(uniqueDevicesPerConversion, {
                maximumFractionDigits: 2,
              })}
              content={
                <span>
                  a user encountered ads on approximately{' '}
                  {formatNumber(uniqueDevicesPerConversion, {
                    maximumFractionDigits: 2,
                  })}{' '}
                  <strong>devices</strong> before the user {t(`info_verb.${pathGoal}`)}
                </span>
              }
              isLoading={isLoadingImpressionData}
              isPrevious={isPreviousImpressionData}
              shimmer={false}
            />
            <HighlightCard
              value={formatNumber(uniqueSitesPerConversion, {
                maximumFractionDigits: 2,
              })}
              content={
                <span>
                  a user encountered ads on approximately{' '}
                  {formatNumber(uniqueSitesPerConversion, {
                    maximumFractionDigits: 2,
                  })}{' '}
                  <strong>sites</strong> before the user {t(`info_verb.${pathGoal}`)}
                </span>
              }
              isLoading={isLoadingImpressionData}
              isPrevious={isPreviousImpressionData}
              shimmer={false}
            />
            <HighlightCard
              value={
                pathGoal === 'sales'
                  ? salePathsMetrics?.salesFirstTouchCount
                  : salePathsMetrics?.leadsFirstTouchCount
              }
              content={
                <span>
                  number of users who saw an impression before visiting your website
                </span>
              }
              isLoading={isLoadingImpressionData}
              isPrevious={isPreviousImpressionData}
              shimmer={false}
            />
          </div>
          <div
            className={classNames('grid grid-cols-2 gap-4', {
              'opacity-50': isLoadingImpressionData || isPreviousImpressionData,
            })}>
            <div className={'rounded-xl flex flex-col p-5 gap-4 bg-light-cyan'}>
              <h1 className={'text-sm font-bold'}>
                {mostEfficientDevice?.deviceType} impressions
              </h1>
              <p>
                Users needed the <b>fewest number of impressions to convert</b> when{' '}
                <b>{mostEfficientDevice?.deviceType}</b> was on the conversion path - it
                only took an average of{' '}
                {formatNumber(mostEfficientDevice?.avgImpressions, {
                  maximumFractionDigits: 2,
                })}{' '}
                impressions for a user to convert.
              </p>
            </div>
            <div className={'rounded-xl flex flex-col p-5 gap-4 bg-light-cyan'}>
              <h1 className={'text-sm font-bold'}>{shortestConversionTimeSite?.site}</h1>
              <p>
                When <b>{shortestConversionTimeSite?.site}</b> was on the conversion path,{' '}
                <b>the time to convert was the shortest</b>. On average, it only took{' '}
                <b>
                  {formatNumber(shortestConversionTimeSite?.avgTime, {
                    maximumFractionDigits: 2,
                  })}
                </b>{' '}
                days for a user to convert after the last impression.
              </p>
            </div>
          </div>
          <div
            className={classNames('grid grid-cols-2 auto-rows-fr gap-4', {
              'opacity-50': isPreviousDataOverallMetrics,
            })}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>{t(pathGoal)}s by Campaign</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <PolarAreaChart
                  data={campaignSales.sort((a, b) => (a.value < b.value ? 1 : -1)) ?? []}
                  isLoading={isLoadingImpressionData}
                  isPreviousData={isPreviousImpressionData}
                  chartScale={'linear'}
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Impressions by Campaign</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <PolarAreaChart
                  data={
                    campaignImpressions.sort((a, b) => (a.value < b.value ? 1 : -1)) ?? []
                  }
                  isLoading={isLoadingImpressionData}
                  isPreviousData={isPreviousImpressionData}
                  chartScale={'linear'}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 ">
            <div className={'min-h-[300px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Top Devices</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <PolarAreaChart
                  data={
                    deviceImpressions
                      .filter(({ label }) => label)
                      .sort((a, b) => (a.value < b.value ? 1 : -1))
                      .slice(0, 5) ?? []
                  }
                  isLoading={isLoadingImpressionData}
                  isPreviousData={isPreviousImpressionData}
                  chartScale={'linear'}
                />
              </div>
            </div>
            <div className={'min-h-[300px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Impression Map</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <MapWithBubbles
                  data={
                    impressionsData
                      ?.filter((d) => d.latitude && d.longitude)
                      ?.map((d) => ({ LATITUDE: d.latitude, LONGITUDE: d.longitude })) ??
                    []
                  }
                  isLoading={isLoadingImpressionData}
                />
              </div>
            </div>
          </div>
          {dealership?.industry === 'retail' && (
            <div className="flex flex-col gap-8">
              <div
                className={classNames('grid grid-cols-2 auto-rows-fr gap-4', {
                  'opacity-50': isPreviousDataOverallMetrics,
                })}>
                <div className={'min-h-[270px] border-2 rounded-xl'}>
                  <div
                    className={
                      'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                    }>
                    <span>{t(pathGoal)} by Audience</span>
                  </div>
                  <div className={'h-[270px] p-2'}>
                    <PolarAreaChart
                      data={
                        audienceSales
                          .sort((a, b) => (a.value < b.value ? 1 : -1))
                          ?.slice(0, 5) ?? []
                      }
                      isLoading={isLoadingImpressionData}
                      isPreviousData={isPreviousImpressionData}
                      chartScale={'linear'}
                    />
                  </div>
                </div>
                <div className={'min-h-[270px] border-2 rounded-xl'}>
                  <div
                    className={
                      'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                    }>
                    <span>{t(pathGoal)} by Ad Group</span>
                  </div>
                  <div className={'h-[270px] p-2'}>
                    <PolarAreaChart
                      data={
                        vendorCampaignSales.sort((a, b) =>
                          a.value < b.value ? 1 : -1,
                        ) ?? []
                      }
                      isLoading={isLoadingImpressionData}
                      isPreviousData={isPreviousImpressionData}
                      chartScale={'linear'}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Impression Distribution by Customer</span>
                <Info message="Shows how many customers saw your ads a specific number of times before purchasing." />
              </div>
              <div className={'h-[270px] p-2'}>
                <BarChart
                  data={
                    impressionRangeCounts?.map((d) => {
                      return {
                        label: d.range,
                        value: d.count,
                      }
                    }) ?? []
                  }
                  gradient={'green-to-lemon-green'}
                  isLoading={isLoadingImpressionData}
                  isPreviousData={isPreviousImpressionData}
                  customOptions={{
                    legend: {
                      display: false,
                    },
                  }}
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Impression Decay</span>
                <Info message="Shows when customers viewed your ads relative to their purchase date, revealing which timeframes influenced buying decisions." />
              </div>
              <div className={'h-[270px] p-2'}>
                <BarChart
                  data={
                    impressionDecayAnalysis?.map((d) => {
                      return {
                        label: d.range,
                        value: d.count,
                      }
                    }) ?? []
                  }
                  gradient={'blue-to-purple'}
                  isLoading={isLoadingImpressionData}
                  isPreviousData={isPreviousImpressionData}
                  customOptions={{
                    legend: {
                      display: false,
                    },
                  }}
                />
              </div>
            </div>
          </div>

          <div className={'min-h-[270px] border-2 rounded-xl pb-3'}>
            <div className="pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic">
              <span>Top Sites</span>
            </div>

            <motion.div
              className="p-2 overflow-hidden"
              initial={false}
              animate={{
                height: topSitesExpanded ? 450 : 270,
              }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}>
              <HorizontalBarChart
                data={topSitesData}
                yKey="people"
                isLoading={isLoadingImpressionData}
                isPreviousData={isPreviousImpressionData}
              />
            </motion.div>

            {topSites?.length > 10 && (
              <div className="w-fit mx-auto">
                <Button
                  variant="rainbow"
                  onClick={(e) => {
                    e.preventDefault()
                    setTopSitesExpanded(!topSitesExpanded)
                  }}>
                  {topSitesExpanded ? 'Show less' : 'Show more'}
                </Button>
              </div>
            )}
          </div>
          {pathGoal === 'sales' && (
            <>
              <div className="flex flex-col gap-3 py-3">
                <div className="flex flex-row gap-2 items-center">
                  <span className="font-bold text-sm">Verified {t(pathGoal)}s</span>
                  <Info
                    message={`Users seen in the DMS who were exposed to an ad and ${t(
                      'info_verb.sales',
                    )}`}
                  />
                </div>
                <Table
                  columns={verifiedSalesColumns}
                  filters={{
                    start_date: filters.dateRange.startDate,
                    end_date: filters.dateRange.endDate,
                  }}
                  fetchFn={useGetSalePathsVerifiedSales}
                  getRowCanExpand={() => true}
                  renderSubComponent={SalesUserPath}
                />
              </div>
              <div className="flex flex-col gap-3 pt-3">
                <div className="flex flex-row justify-between items-center">
                  <div className="flex flex-row gap-2 items-center">
                    <span className="font-bold text-sm">Potential {t('sales')}s</span>
                    <Info
                      message={`Users who were exposed to an ad and submitted a lead but haven't ${t(
                        'info_verb.sales',
                      )}`}
                    />
                  </div>
                  <Button
                    disabled={downloadingTable}
                    variant="primary"
                    onClick={async (e) => {
                      e.preventDefault()
                      const toastId = toast.loading('Downloading...')
                      setDownloadingTable(true)
                      try {
                        const headers = potentialSalesColumns.map((column) => ({
                          // @ts-ignore
                          accessorKey: column.accessorKey,
                          header:
                            typeof column.header === 'function'
                              ? column.accessorKey
                              : column.header,
                        }))
                        await downloadCSV({
                          url: `/agencies/${dealership?.organization_id}/dealership/${dealership?.id}/performance/sale-paths/verified-leads`,
                          fetchOptions: {
                            start_date: filters?.dateRange?.startDate,
                            end_date: filters?.dateRange?.endDate,
                            excludeSales: 1,
                          },
                          headers: headers,
                          fileName: `${dealership?.dealership}-PotentialSales_${filters.dateRange.startDate}-${filters.dateRange.endDate}.csv`,
                        })
                        toast.success('Downloaded table!', {
                          id: toastId,
                          duration: 5000,
                        })
                        setDownloadingTable(false)
                      } catch (err) {
                        toast.error('Failed to download table at this time.', {
                          id: toastId,
                          duration: 5000,
                        })
                        setDownloadingTable(false)
                      }
                    }}>
                    Export CSV
                  </Button>
                </div>

                <Table
                  columns={potentialSalesColumns}
                  filters={{
                    start_date: filters.dateRange.startDate,
                    end_date: filters.dateRange.endDate,
                    excludeSales: 1,
                  }}
                  fetchFn={useGetVerifiedLeads}
                  getRowCanExpand={() => true}
                  renderSubComponent={LeadUserPath}
                />
              </div>
            </>
          )}
          {pathGoal === 'leads' && (
            <div className="flex flex-col gap-3 pt-3">
              <div className="flex flex-row justify-between items-center">
                <div className="flex flex-row gap-2 items-center">
                  <span className="font-bold text-sm">Verified {t('leads')}s</span>
                  <Info
                    message={`Users who were exposed to an ad and submitted a lead`}
                  />
                </div>
                <Button
                  disabled={downloadingTable}
                  variant="primary"
                  onClick={async (e) => {
                    e.preventDefault()
                    const toastId = toast.loading('Downloading...')
                    setDownloadingTable(true)
                    try {
                      const headers = potentialSalesColumns.map((column) => ({
                        // @ts-ignore
                        accessorKey: column.accessorKey,
                        header:
                          typeof column.header === 'function'
                            ? column.accessorKey
                            : column.header,
                      }))
                      await downloadCSV({
                        url: `/agencies/${dealership?.organization_id}/dealership/${dealership?.id}/performance/sale-paths/verified-leads`,
                        fetchOptions: {
                          start_date: filters?.dateRange?.startDate,
                          end_date: filters?.dateRange?.endDate,
                          excludeSales: 0,
                        },
                        headers: headers,
                        fileName: `${dealership?.dealership}-PotentialSales_${filters.dateRange.startDate}-${filters.dateRange.endDate}.csv`,
                      })
                      toast.success('Downloaded table!', {
                        id: toastId,
                        duration: 5000,
                      })
                      setDownloadingTable(false)
                    } catch (err) {
                      toast.error('Failed to download table at this time.', {
                        id: toastId,
                        duration: 5000,
                      })
                      setDownloadingTable(false)
                    }
                  }}>
                  Export CSV
                </Button>
              </div>

              <Table
                columns={potentialSalesColumns}
                filters={{
                  start_date: filters.dateRange.startDate,
                  end_date: filters.dateRange.endDate,
                  excludeSales: 0,
                }}
                fetchFn={useGetVerifiedLeads}
                getRowCanExpand={() => true}
                renderSubComponent={LeadUserPath}
              />
            </div>
          )}

          <div className="flex flex-col gap-3 py-3">
            <div className="flex flex-row gap-2 items-center">
              <span className="font-bold text-sm">Top UTM Sources</span>
              <Info message={'Top sites where users saw an impression'} />
            </div>
            <Table
              columns={topSitesColumns}
              filters={{
                start_date: filters.dateRange.startDate,
                end_date: filters.dateRange.endDate,
                goal: pathGoal,
              }}
              defaultSorting={[
                {
                  id: 'people',
                  desc: true,
                },
              ]}
              fetchFn={useGetUTMSources}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
